import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ISupplier,
  ISupplierDetail,
  IDropdownSupplier,
  SupplierState,
  ISupplierForm,
} from '../../../types/supplier/supplierInterfaces';

export const initialFormState: ISupplierForm = {
  id: null,
  name: '',
  nit: '',
  nrc: '',
  dui: '',
  passportNumber: '',
  residentCardNumber: '',
  otherIdentification: '',
  legalName: '',
  tradeName: '',
  phone: '',
  email: '',
  complement: '',
  entityTypeId: null,
  taxpayerCategoryId: null,
  economicActivityId: null,
  departmentId: null,
  departmentCode: '',
  municipalityId: null,
  documentIdentificationTypeId: null,
  documentIdentificationTypeCode: '',
  status: true,
};

const initialState: SupplierState = {
  form: initialFormState,
  detail: null,
  list: [],
  paginator: null,
  isLoading: false,
  isSubmitting: false,
  errors: null,
  suppliersDropdown: [],
  showRegistrationModal: false,
  showViewModal: false,
  showEditModal: false,
};

const supplierConfigurationSlice = createSlice({
  name: 'supplierConfiguration',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },

    // Guardar lista de proveedores
    setSupplierList(state, action: PayloadAction<ISupplier[]>) {
      state.list = action.payload;
    },
    addSupplierToList(state, action: PayloadAction<ISupplier>) {
      state.list = [action.payload, ...state.list];
    },

    setPaginator(state, action: PayloadAction<any>) {
      state.paginator = action.payload;
    },

    // Dropdown de proveedores
    setSuppliersDropdown(state, action: PayloadAction<IDropdownSupplier[]>) {
      state.suppliersDropdown = action.payload;
    },

    // Detalle para ver o editar
    setSupplierDetail(state, action: PayloadAction<ISupplierDetail>) {
      state.detail = action.payload;
      state.form = {
        id: action.payload.id ?? null,
        name: action.payload.name ?? '',
        nit: action.payload.nit ?? '',
        nrc: action.payload.nrc ?? '',
        dui: action.payload.dui ?? '',
        passportNumber: action.payload.passportNumber ?? '',
        residentCardNumber: action.payload.residentCardNumber ?? '',
        otherIdentification: action.payload.otherIdentification ?? '',
        legalName: action.payload.legalName ?? '',
        tradeName: action.payload.tradeName ?? '',
        phone: action.payload.phone ?? '',
        email: action.payload.email ?? '',
        complement: action.payload.complement ?? '',
        entityTypeId: null, // se llena desde otro fetch si aplicara
        taxpayerCategoryId: null,
        economicActivityId: null,
        departmentId: null,
        departmentCode: '',
        municipalityId: null,
        documentIdentificationTypeId: null,
        documentIdentificationTypeCode: '',
        status: action.payload.status ?? true,
      };
      state.errors = null;
    },

    // Formulario cargado para edición
    setSupplierFormForEdit(state, action: PayloadAction<ISupplierForm>) {
      const data = action.payload;
      state.form = {
        id: data.id,
        name: data.name ?? '',
        nit: data.nit ?? '',
        nrc: data.nrc ?? '',
        dui: data.dui ?? '',
        passportNumber: data.passportNumber ?? '',
        residentCardNumber: data.residentCardNumber ?? '',
        otherIdentification: data.otherIdentification ?? '',
        legalName: data.legalName ?? '',
        tradeName: data.tradeName ?? '',
        phone: data.phone ?? '',
        email: data.email ?? '',
        complement: data.complement ?? '',
        entityTypeId: data.entityTypeId ?? null,
        taxpayerCategoryId: data.taxpayerCategoryId ?? null,
        economicActivityId: data.economicActivityId ?? null,
        departmentId: data.departmentId ?? null,
        departmentCode: data.departmentCode ?? '',
        municipalityId: data.municipalityId ?? null,
        documentIdentificationTypeId: data.documentIdentificationTypeId ?? null,
        documentIdentificationTypeCode: data.documentIdentificationTypeCode ?? '',
        status: data.status ?? true,
      };
      state.errors = null;
    },

    // Actualizar campo del formulario
    updateField(
      state,
      action: PayloadAction<{
        field: keyof ISupplierForm;
        value: any;
      }>
    ) {
      const { field, value } = action.payload;
      state.form = {
        ...state.form,
        [field]: value,
      };
    },

    // Resetear formulario
    resetSupplierForm(state) {
      state.form = initialFormState;
      state.detail = null;
      state.isSubmitting = false;
      state.errors = null;
    },

    // Control de modales
    setShowRegistrationModal(state, action: PayloadAction<boolean>) {
      state.showRegistrationModal = action.payload;
    },
    setShowViewModal(state, action: PayloadAction<boolean>) {
      state.showViewModal = action.payload;
    },
    setShowEditModal(state, action: PayloadAction<boolean>) {
      state.showEditModal = action.payload;
    },

    // Actualizar estado activo/inactivo
    updateSupplierStatusInList(
      state,
      action: PayloadAction<{ id: number; status: boolean }>
    ) {
      const { id, status } = action.payload;
      state.list = state.list.map((supplier) =>
        supplier.id === id ? { ...supplier, status } : supplier
      );
    },

    // Errores
    setErrors(state, action: PayloadAction<Record<string, string> | null>) {
      state.errors = action.payload;
    },

    clearSupplierList(state) {
      state.list = [];
      state.paginator = null;
    },
  },
});

export const {
  setLoading,
  setSubmitting,
  setSupplierList,
  addSupplierToList,
  setPaginator,
  setSuppliersDropdown,
  setSupplierDetail,
  setSupplierFormForEdit,
  updateField,
  resetSupplierForm,
  setShowRegistrationModal,
  setShowViewModal,
  setShowEditModal,
  updateSupplierStatusInList,
  setErrors,
  clearSupplierList,
} = supplierConfigurationSlice.actions;

export default supplierConfigurationSlice.reducer;
